import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { Container, Header, Input, Grid, Button, InputProps, Form, DropdownProps, Label } from "semantic-ui-react";

import AliancaMenu from "../../components/Menu";
import Footer from "../../components/Footer";
import api from "../../services/api";

import TableApp, { ITableAppRender } from "../../components/TableApp";
import MaskedInput from "../../components/MaskedInput";

import { IKit } from "../../interfaces";
import KitRepository from "../../Repositories/KitRepository";
import OrderRepository, { IOrderPostModel } from "../../Repositories/OrderRepository";
import RegionalManagerRepository, { IRegionalManagerListModel } from "../../Repositories/RegionalManagerRepository";
import SectorManagerRepository, { ISectorManagerListModel } from "../../Repositories/SectorManagerRepository";
import AgentsRepository, { IAgentListModel } from "../../Repositories/AgentsRepository";

import NumberFormat from "../../Utils/NumberFormat";
import errors from "../../Utils/ExtractErrors";

interface IKitState {
    kits: IKit[];
    loading: boolean;
}

interface IItemModel extends IKit {
    warning: boolean;
    item: number;
    valorCurrency: string;
}

const EditarPedido: React.FC = () => {

    const [loading, setLoading] = useState<boolean>(false);
    const [stateKit, setStateKit] = useState<IKitState>({ kits: [], loading: false });
    const [numero, setNumero] = useState<string>("");

    const [order, setOrder] = useState<IOrderPostModel>({ acerto: "", gerenteRegionalId: "", status: 1, gerenteSetorId: "", representanteId: "" });
    const [items, setItems] = useState<IKit[]>([]);
    const [tableList, setTablelist] = useState<IItemModel[]>([]);

    const [regionalManagers, setRegionalManagers] = useState<IRegionalManagerListModel[]>([]);
    const [sectorManagers, setSectorManagers] = useState<ISectorManagerListModel[]>([]);
    const [agents, setAgents] = useState<IAgentListModel[]>([]);

    const LoadKits = useCallback(async () => {
        setStateKit(stateKit => ({ ...stateKit, loading: true }));

        const result = await KitRepository.ListStocked();

        setStateKit({ kits: result, loading: false });
    }, []);

    const LoadRegionalManagers = useCallback(async () => {
        const result = await RegionalManagerRepository.List({
            filter: "Status == 1"
        });

        setRegionalManagers(result.list);
    }, []);

    useEffect(() => { LoadRegionalManagers() }, [LoadRegionalManagers]);

    const LoadSectorManagers = useCallback(async (id: string) => {
        const result = await SectorManagerRepository.List({
            filter: `GerenteRegionalId == "${id}" && Status == 1`
        });

        setSectorManagers(result.list);
    }, []);

    const LoadAgents = useCallback(async (id: string) => {
        const result = await AgentsRepository.List({
            filter: `GerenteSetorId == "${id}" && Status == 1`
        });

        setAgents(result.list);
    }, []);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNumero(e.target.value);
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter")
            handleAddItem();
    }

    const handleAddItem = () => {
        const duplicatedItem = items.find(x => x.numero === Number(numero));

        if (duplicatedItem) {
            toast(`Kit nº ${duplicatedItem.numero} já adicionado`, { position: "top-center", type: "error" });
        } else {
            const item = stateKit.kits.find(x => x.numero === Number(numero));

            if (item !== undefined) {
                let addKit = false;

                if (item.statusKit === 2) {
                    if (item.prorrogado === 1) {
                        toast(`Kit nº ${numero} não pode mais ser prorrogado`, { position: "top-center", type: "error" });
                    } else {
                        item.prorrogado = 1;
                        addKit = true;
                    }
                } else {
                    addKit = true;
                }

                if (addKit) {
                    setNumero("");
                    setItems([item, ...items]);
                }
            }
            else {
                toast(`Kit nº ${numero} não encontrado`, { position: "top-center", type: "error" });
            }
        }
    }

    const handleRemoveItem = (index: number) => {
        const item = items.find((x, i) => i === index);

        if (item !== undefined) {
            const newItems = items.filter((x, i) => i !== index);

            setItems(newItems);
        }
    }

    const removeButton = (index: number) => <Button icon="trash" color="red" onClick={() => handleRemoveItem(index)} />

    const handleTableData = useCallback(() => {

        let data: IItemModel[] = [];
        let contador: number = items.length;

        items.map((kit) => {
            return data.push({
                item: contador--,
                ...kit,
                warning: kit.prorrogado === 1,
                valorCurrency: NumberFormat.Currency(kit.valor)
            });
        });

        setTablelist(data);
    }, [items]);

    const handlePrint = (id: string) => {
        var a = document.createElement("a");
        a.href = `${api.defaults.baseURL}/pedido/${id}/print`;
        a.setAttribute("target", "_blank");
        a.click();
    }

    const handleFinalizar = async () => {
        setLoading(true);

        if (items.length > 0) {
            try {
                const response = await OrderRepository.Insert({
                    orderPostModel: order,
                    kits: items
                });

                toast(`🎉 ${response.message}`, { position: "top-center", type: "success" });
                handlePrint(response.pedido.id || "");
                window.location.reload();
            } catch (err) {
                if (err.response.status === 401) {
                    window.location.href = "/";
                }

                const error = errors.getAllErrors(err.response.data);

                if (error !== undefined)
                    toast(error, { position: "top-center", type: "error" });
            }
        } else {
            toast("🤷‍♂️ Adicione kits no pedido.", { position: "top-center", type: "error" });
        }
        setLoading(false);
    }

    const setWarning = (prorrogado: number, numero: number) => {
        if (prorrogado === 1) {
            return <Label icon="warning sign" color="orange" size="large" content={numero} />
        } else {
            return numero;
        }
    }

    useEffect(() => { LoadKits() }, [LoadKits]);

    useEffect(() => { handleTableData() }, [handleTableData, items]);

    const tableRender: ITableAppRender[] = [
        {
            header: "Item",
            content: (x: IItemModel) => x.item,
            textAlign: "center",
            width: 1
        },
        {
            header: "Número do kit",
            content: (x: IItemModel) => setWarning(x.prorrogado, x.numero),
            textAlign: "left",
            width: 10
        },
        {
            header: "Valor",
            content: (x: IItemModel) => x.valorCurrency,
            textAlign: "center",
            width: 1
        },
    ];

    const onRegionalManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;

        setOrder({ ...order, [name]: value, gerenteSetorId: "", representanteId: "" });

        LoadSectorManagers(value?.toString() || "");
    }

    const onSectorManagerChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;

        setOrder({ ...order, [name]: value, representanteId: "" });

        LoadAgents(value?.toString() || "");
    }

    const onAgentChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;

        setOrder({ ...order, [name]: value });
    }

    return (
        <>
            <AliancaMenu />
            <Container className="app-margin">
                <Grid columns="equal">
                    <Grid.Row>
                        <Grid.Column>
                            <Header size="huge" content="Novo pedido" icon="list alternate" />
                        </Grid.Column>
                        <Grid.Column>
                            <MaskedInput
                                fluid
                                mask="data"
                                icon="calendar alternate"
                                iconPosition="left"
                                placeholder="Data acerto"
                                value={order.acerto}
                                onChange={(e: InputProps) => setOrder({ ...order, acerto: e.target.value })}
                                autoFocus
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>
                            <Form>
                                <Form.Group widths="equal">
                                    <Form.Field>
                                        <Form.Dropdown
                                            fluid
                                            selection
                                            search
                                            scrolling
                                            name="gerenteRegionalId"
                                            noResultsMessage="Nenhum registro encontrado."
                                            placeholder="Selecione"
                                            label="G. Regional"
                                            value={order.gerenteRegionalId}
                                            onChange={onRegionalManagerChange}
                                            options={regionalManagers.map(regionalManager => { return { key: regionalManager.id, value: regionalManager.id, text: regionalManager.nome } })}
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <Form.Dropdown
                                            fluid
                                            selection
                                            search
                                            scrolling
                                            disabled={order.gerenteRegionalId.length === 0}
                                            name="gerenteSetorId"
                                            noResultsMessage="Nenhum registro encontrado."
                                            placeholder="Selecione"
                                            label="G. Setor"
                                            value={order.gerenteSetorId}
                                            onChange={onSectorManagerChange}
                                            options={sectorManagers.map(sectorManager => { return { key: sectorManager.id, value: sectorManager.id, text: sectorManager.nome } })}
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <Form.Dropdown
                                            fluid
                                            selection
                                            search
                                            scrolling
                                            disabled={(order.gerenteSetorId?.length || "") === ""}
                                            name="representanteId"
                                            noResultsMessage="Nenhum registro encontrado."
                                            placeholder="Selecione"
                                            label="Representante"
                                            value={order.representanteId}
                                            onChange={onAgentChange}
                                            options={agents.map(agent => { return { key: agent.id, value: agent.id, text: agent.nome } })}
                                        />
                                    </Form.Field>
                                </Form.Group>
                            </Form>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>
                            <Input
                                fluid
                                label="Número do kit"
                                placeholder="Insira o número do kit"
                                onChange={handleOnChange}
                                onKeyDown={handleOnKeyDown}
                                value={numero}
                                action={{ icon: "add", color: "pink", onClick: handleAddItem }}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column>
                            <Button
                                fluid
                                icon="print"
                                color="blue"
                                content="Finalizar e imprimir pedido"
                                onClick={handleFinalizar}
                                loading={loading}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Column>
                        <Grid.Row>
                            <TableApp
                                render={tableRender}
                                list={tableList}
                                actions={(data: IItemModel, index: number) => [removeButton(index)]}
                                loading={false}
                            />
                        </Grid.Row>
                    </Grid.Column>
                </Grid>
            </Container>
            <Footer />
        </>
    );
};

export default EditarPedido;
